import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import cn from 'classnames';
import { Grid } from 'ht-styleguide';

/* Components */
import Either from '@components/Either';
import WalledGarden from '@components/WalledGarden';
import SlideModal from '@components/Modals/Slide';
import FooterSocial from './FooterSocial';

/* Types */
import { PartnerBaseAttributes } from '@integrations/partners/partners.types';

/* Constants / Utils */
import { cancellationPolicyPath, cookiesPath, privacyPath, snsPath, tosPath } from '@constants/paths';
import { prevented } from '@utils/event';

/* Styles */
import styles from '../footer.module.scss';

/* Dynamic Includes */
const StandardTOSDynamic = dynamic(() => import('@scenes/TosPage/TosPage'), { ssr: false });
// @ts-ignore
const CancellationPolicyDynamic = dynamic(() => import('@pages/cancellation-policy').then(mod => mod.CancellationPolicyIframe), { ssr: false });
const SafetyandSupportDynamic = dynamic(() => import('@scenes/SafetyAndSupportPage/SafetyAndSupportPage'), { ssr: false });
// @ts-ignore
const PrivacyIframeDyamic = dynamic(() => import('@pages/privacy').then(mod => mod.PrivacyIframe), { ssr: false });
const CookiesPolicyDyamic = dynamic(() => import('@pages/cookies'), { ssr: false });

const DefaultLegalSection = () => (
  <>
    <Link href={privacyPath()} className={styles.footerLineItem}>
      Privacy Policy
    </Link>
    <Link href={tosPath()} className={styles.footerLineItem}>
      Terms of Services
    </Link>
    <Link href={snsPath()} className={styles.footerLineItem}>
      Safety & Support
    </Link>
    <Link href={cancellationPolicyPath()} className={styles.footerLineItem}>
      Cancellation Policy
    </Link>
    <Link href={cookiesPath()} className={styles.footerLineItem}>
      Cookies
    </Link>
  </>
);

const PartnerLegalSection = () => {
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const openModal = (modalKey: string) => (e?: React.SyntheticEvent) => {
    setActiveModal(modalKey);
  };

  const handleClose = () => {
    setActiveModal(null);
  };

  return (
    <>
      <Link href={privacyPath()} prefetch={false} className={styles.footerLineItem} onClick={prevented(openModal('privacy'))}>
        Privacy Policy
      </Link>
      <Link href={tosPath()} prefetch={false} className={styles.footerLineItem} onClick={prevented(openModal('tos'))}>
        Terms of Services
      </Link>
      <Link href={snsPath()} prefetch={false} className={styles.footerLineItem} onClick={prevented(openModal('safety'))}>
        Safety & Support
      </Link>
      <Link href={cancellationPolicyPath()} prefetch={false} className={styles.footerLineItem} onClick={prevented(openModal('cancellation'))}>
        Cancellation Policy
      </Link>
      <Link href={cookiesPath()} prefetch={false} className={styles.footerLineItem} onClick={prevented(openModal('cookies'))}>
        Cookies
      </Link>
      {activeModal === 'privacy' && (
        <SlideModal open={true} handleClose={handleClose}>
          <PrivacyIframeDyamic />
        </SlideModal>
      )}
      {activeModal === 'tos' && (
        <SlideModal open={true} handleClose={handleClose}>
          <StandardTOSDynamic />
        </SlideModal>
      )}
      {activeModal === 'safety' && (
        <SlideModal open={true} handleClose={handleClose}>
          <SafetyandSupportDynamic />
        </SlideModal>
      )}
      {activeModal === 'cancellation' && (
        <SlideModal open={true} handleClose={handleClose}>
          <CancellationPolicyDynamic />
        </SlideModal>
      )}
      {activeModal === 'cookies' && (
        <SlideModal open={true} handleClose={handleClose}>
          <CookiesPolicyDyamic />
        </SlideModal>
      )}
    </>
  );
};

const LegalSection = ({ partner }: { partner?: PartnerBaseAttributes }) => {
  const year = new Date().getFullYear();

  return (
    <Grid.Fluid>
      <Grid.Row>
        <Grid.Column sm={4} mdOffset={1} md={6} lgOffset={1} lg={10}>
          <div className={cn(styles.legalSection, 'paddingY-medium')}>
            <div className={styles.legalItemsContainer}>
              <Either condition={Boolean(partner?.id)}>
                <PartnerLegalSection />
                <DefaultLegalSection />
              </Either>
            </div>
            <div className={styles.copyrightNotice}>
              <p className="p2 n700">&#169; {year} HelloTech. All Rights Reserved.</p>
              <WalledGarden type="footerSocial" condition={partner?.id}>
                <FooterSocial />
              </WalledGarden>
            </div>
          </div>
        </Grid.Column>
      </Grid.Row>
    </Grid.Fluid>
  );
};

export default LegalSection;

/*
  LegalSection: Section of the footer that contains links to legal info; the bottom portion of the complete header.

  Props
  ------
    - hideSocial(boolean): Our social media links are hidden for certain 3rd party partners pages
*/
